// services/admin/adminChamados.service.ts
// Atendimento dos chamados pelo lado da Intrex.
//
// As rotas /api/admin/chamados respondem no formato { ok, ... } direto, sem o
// envelope { ok, data } de adminCommon.types — igual às rotas de regimes e
// carteira. Por isso os tipos aqui descrevem o corpo inteiro.
import { ADMIN_API } from "./adminApi";

export type ChamadoStatus = "ACAO_CLIENTE" | "EM_ANALISE" | "RESOLVIDO";

export type ChamadoTipo =
  | "ALFANDEGA"
  | "ENDERECO"
  | "ATRASO"
  | "TENTATIVA_ENTREGA"
  | "AVARIA"
  | "OUTRO";

export type ChamadoAutor = "CLIENTE" | "INTREX" | "TRANSPORTADORA";

export type ChamadoMensagem = {
  id: number;
  autor: ChamadoAutor;
  texto: string | null;
  anexo_nome: string | null;
  tem_anexo: boolean;
  criado_em: string;
};

export type AdminChamado = {
  id: number;
  cliente_id: number;
  cotacao_id: number | null;
  tipo: ChamadoTipo;
  titulo: string;
  descricao: string | null;
  acao: string | null;
  status: ChamadoStatus;
  prazo: string | null;
  origem: "AUTO" | "CLIENTE";
  criado_em: string;
  atualizado_em: string | null;
  resolvido_em: string | null;
  cliente: { id: number; razaoSocial: string; emailPrincipal: string } | null;
  envio: {
    pedido_ref: string;
    carrier: string;
    tracking_number: string | null;
    comprador: string | null;
    destino: string;
  } | null;
};

export type AdminChamadosResposta = {
  ok: true;
  page: number;
  pageSize: number;
  total: number;
  resumo: {
    ACAO_CLIENTE: number;
    EM_ANALISE: number;
    RESOLVIDO: number;
    abertos: number;
    aguardando_intrex: number;
  };
  chamados: AdminChamado[];
};

export type AdminChamadoDetalhe = {
  ok: true;
  chamado: AdminChamado;
  tracking_raw: unknown;
  status_envio: string | null;
  telefone: string | null;
  opcoes: { status: ChamadoStatus[]; tipos: ChamadoTipo[] };
  mensagens: ChamadoMensagem[];
};

export type AdminChamadoFiltros = {
  page?: number;
  pageSize?: number;
  status?: ChamadoStatus | "";
  tipo?: ChamadoTipo | "";
  origem?: "AUTO" | "CLIENTE" | "";
  cliente_id?: string | number;
  abertos?: "1" | "";
  q?: string;
};

export const listAdminChamados = async (filtros: AdminChamadoFiltros = {}) => {
  // Campo vazio no params viraria `?status=` e o backend trataria como filtro
  // inválido em vez de "sem filtro".
  const params = Object.fromEntries(
    Object.entries(filtros).filter(([, v]) => v !== "" && v !== undefined && v !== null)
  );
  const { data } = await ADMIN_API.get<AdminChamadosResposta>("/api/admin/chamados", { params });
  return data;
};

export const getAdminChamado = async (id: string | number) => {
  const { data } = await ADMIN_API.get<AdminChamadoDetalhe>(`/api/admin/chamados/${id}`);
  return data;
};

export type RespostaChamado = {
  texto?: string;
  status?: ChamadoStatus;
  anexo?: { base64: string; mime: string; nome: string };
};

export const responderChamado = async (id: string | number, corpo: RespostaChamado) => {
  const { data } = await ADMIN_API.post<{
    ok: true;
    mensagem: ChamadoMensagem;
    status: ChamadoStatus;
  }>(`/api/admin/chamados/${id}/mensagens`, corpo);
  return data;
};

export type PatchChamado = Partial<{
  tipo: ChamadoTipo;
  titulo: string;
  descricao: string | null;
  acao: string | null;
  prazo: string | null;
  status: ChamadoStatus;
}>;

export const atualizarChamado = async (id: string | number, patch: PatchChamado) => {
  const { data } = await ADMIN_API.patch<{ ok: true; chamado: AdminChamado }>(
    `/api/admin/chamados/${id}`,
    patch
  );
  return data;
};

/**
 * Baixa o anexo da conversa. Vem como binário, então é preciso montar um blob —
 * não dá para apontar um <a href> direto na rota, que exige o Bearer do admin.
 */
export const baixarAnexoChamado = async (
  chamadoId: string | number,
  mensagemId: number,
  nome: string
) => {
  const resp = await ADMIN_API.get(
    `/api/admin/chamados/${chamadoId}/mensagens/${mensagemId}/anexo`,
    { responseType: "blob" }
  );
  const url = window.URL.createObjectURL(resp.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nome || "anexo";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};
